import React, { useState } from 'react';
import {
  Modal,
  Icon,
  Colors,
  Title,
  Body,
  Button,
  Overline,
} from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { MilestoneType } from 'types/dt-graphql-apollo-v2';
import { term } from 'utils/project-ruleset';

export const MilestoneTypeSelectionModal = ({
  visible,
  onSelectMilestoneType,
  onCancel,
}: {
  visible: boolean;
  onSelectMilestoneType: (type: MilestoneType) => void;
  onCancel: VoidFunction;
}) => {
  const [selectedMilestoneType, setSelectedMilestoneType] =
    useState<MilestoneType | null>(null);

  const handleMilestoneSelection = (milestoneType: MilestoneType) => {
    setSelectedMilestoneType(milestoneType);
  };

  const handleCancel = () => {
    setSelectedMilestoneType(null);
    onCancel();
  };

  const handleMilestoneCreate = () => {
    if (selectedMilestoneType) {
      onSelectMilestoneType(selectedMilestoneType);
      setSelectedMilestoneType(null);
    }
  };

  const renderHeader = () => (
    <StyledHeader>
      <StyledTitle level={3}>
        Create {term.rule} {term.collection}
      </StyledTitle>
      <StyledDescription level={5}>
        Create a {term.collection} to organize your {term.rules} and apply a
        time frame for when these {term.rules} are run.
      </StyledDescription>
    </StyledHeader>
  );

  const renderMilestoneOptions = () => (
    <>
      <Overline level={5}>Select type:</Overline>
      <StyledGrid>
        <StyledMilestoneOption
          isSelected={selectedMilestoneType === MilestoneType.Default}
          onClick={() => handleMilestoneSelection(MilestoneType.Default)}
        >
          <div>
            <Icon size={40} type="Flag" />
            <Title level={5}>{term.MilestoneCollection}</Title>
          </div>
          <Body level={5}>
            A {term.milestoneCollection} allows you to modify {term.rules} and{' '}
            {term.rulesets} on a timeline. The changes you make in one{' '}
            {term.milestone} will apply to the following {term.milestones} as
            default.
          </Body>
        </StyledMilestoneOption>

        <StyledMilestoneOption
          isSelected={selectedMilestoneType === MilestoneType.Static}
          onClick={() => handleMilestoneSelection(MilestoneType.Static)}
        >
          <div>
            <Icon size={40} type="World" />
            <Title level={5}>{term.OpenCollection}</Title>
          </div>
          <Body level={5}>
            An {term.openCollection} is a single {term.collection} of{' '}
            {term.rules}, not directly connected to the project timeline.
          </Body>
        </StyledMilestoneOption>
      </StyledGrid>
    </>
  );

  const renderFooterButtons = () => (
    <StyledButtons>
      <Button
        type="ghost"
        onClick={handleCancel}
        style={{ marginRight: '1rem' }}
      >
        Cancel
      </Button>
      <Button
        type="primary"
        disabled={
          !(
            selectedMilestoneType === MilestoneType.Static ||
            selectedMilestoneType === MilestoneType.Default
          )
        }
        onClick={handleMilestoneCreate}
      >
        Select
      </Button>
    </StyledButtons>
  );

  return (
    <StyledModal visible={visible} onCancel={handleCancel} hideFooter>
      {renderHeader()}
      {renderMilestoneOptions()}
      {renderFooterButtons()}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  overflow: hidden;
  width: 760px !important;
  max-width: calc(100% - 2rem);
  .cogs-modal-content {
    padding: 0;
    min-height: 200px;
    position: relative;
    overflow-wrap: break-word;
    margin: 1rem 2rem;
  }
  .cogs-modal-header {
    padding: 0 0 0.5rem 1rem;
    font-size: 1.2rem;
    font-weight: 600;
  }
  .cogs-icon-Close {
    cursor: pointer;
  }
`;

const StyledHeader = styled.div`
  margin-bottom: 2rem;
`;

const StyledTitle = styled(Title)`
  margin: 1rem 0;
`;

const StyledDescription = styled(Body)`
  margin: 1rem 0;
`;

const StyledMilestoneOption = styled.div<{ isSelected: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  border: 2px solid ${Colors['border--interactive--default']};
  border-radius: 10px;
  padding: 2rem 1.5rem;
  transition: all 250ms linear;
  div:first-child {
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
  }
  ${(props) =>
    props.isSelected &&
    `
    background-color: var(--cogs-midblue-8) !important;
    border: 2px solid var(--cogs-midblue-4) !important;
    .cogs-icon {
        align-items: center;
        color: var(--cogs-midblue-4) !important;
    }

  `};

  .cogs-icon {
    align-items: center;
    color: var(--cogs-text-hint);
  }

  .cogs-title-5 {
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0.5rem 0;
  }

  .cogs-body-5 {
    color: var(--cogs-text-hint);
    overflow: auto;
    text-overflow: ellipsis;
  }

  &:hover {
    cursor: pointer;
    background-color: ${Colors['decorative--grayscale--200']};
  }
`;

const StyledButtons = styled.div`
  padding: 2rem 0 0 0;
  text-align: right;
`;

const StyledGrid = styled.div`
  display: grid;
  width: 100%;
  gap: 2rem;
  margin-top: 1rem;
  grid-template-columns: 1fr 1fr;
  > div {
    width: 100%;
    height: 100%;
  }
`;
